import { useState } from "react";

/**
 * composeAddress — Build a US street address string from a Geoapify reverse-geocode
 * response (SPR-021 A-05, Option B).
 *
 * Geoapify (`format=json`) returns `results[]` with housenumber, street,
 * city/town/village, state, postcode, and a `formatted` full-address string —
 * so we get street-level results on a commercial-friendly, keyed API. We build a
 * structured US line, then fall back to `formatted`, then "".
 *
 * @param {object} data - Geoapify reverse JSON ({ results: [ {...} ] })
 * @returns {string} e.g. "320 Silas Creek Pkwy, Winston-Salem, North Carolina 27103"
 */
export function composeAddress(data) {
  if (!data || typeof data !== "object") return "";
  const r =
    (Array.isArray(data.results) && data.results[0]) ||
    data.features?.[0]?.properties ||
    null;
  if (!r) return "";

  const street = [r.housenumber, r.street].filter(Boolean).join(" ");
  const city = r.city || r.town || r.village || r.municipality || r.county || "";
  const state = r.state || "";
  const postcode = r.postcode || "";

  const stateZip = [state, postcode].filter(Boolean).join(" ");
  const composed = [street, city, stateZip].filter(Boolean).join(", ").trim();

  if (composed) return composed;
  return typeof r.formatted === "string" ? r.formatted : "";
}

/**
 * UseMyLocationButton — "Use my location" helper for admin address fields
 * (SPR-021 A-05, Option B). Browser geolocation → Geoapify reverse geocode
 * (street-level, keyed/commercial TOS) → onResolve(address). All failures are
 * surfaced inline (non-blocking); manual entry always remains available.
 *
 * Accuracy depends on the device's geolocation — phones (GPS) give a real street
 * address; desktops (Wi-Fi/IP) are coarser. Attribution to Geoapify/OpenStreetMap
 * is shown beneath the button per the provider's terms.
 *
 * @param {object} props
 * @param {(address: string) => void} props.onResolve
 * @param {string} [props.testId]
 */
export default function UseMyLocationButton({ onResolve, testId = "use-my-location-button" }) {
  const [status, setStatus] = useState(null); // { type: "error"|"info", message }
  const [busy, setBusy] = useState(false);

  const handleClick = () => {
    setStatus(null);

    // Client-exposed but origin-restricted (like the Turnstile site key). Read at
    // call time so tests/config can vary it without a module reload.
    const GEOAPIFY_KEY = process.env.REACT_APP_GEOAPIFY_KEY;
    if (!GEOAPIFY_KEY) {
      setStatus({ type: "error", message: "Location lookup isn't configured." });
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setStatus({ type: "error", message: "Location isn't available on this device." });
      return;
    }

    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(
            `https://api.geoapify.com/v1/geocode/reverse?lat=${latitude}&lon=${longitude}&format=json&apiKey=${GEOAPIFY_KEY}`
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const address = composeAddress(data);
          if (!address) {
            setStatus({ type: "error", message: "Couldn't determine an address from your location." });
          } else {
            onResolve(address);
            setStatus({ type: "info", message: "Location filled in — refine if needed." });
          }
        } catch {
          setStatus({ type: "error", message: "Couldn't look up your address. Enter it manually." });
        } finally {
          setBusy(false);
        }
      },
      (err) => {
        setBusy(false);
        const message =
          err && err.code === err.PERMISSION_DENIED
            ? "Location permission denied. Enter the address manually."
            : "Couldn't get your location. Enter the address manually.";
        setStatus({ type: "error", message });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        data-testid={testId}
        className="font-heading tracking-wider text-xs text-teal border-2 border-teal px-3 py-1 rounded-full hover:bg-teal hover:text-white transition-colors disabled:opacity-50"
      >
        {busy ? "LOCATING…" : "USE MY LOCATION"}
      </button>
      {status && (
        <p
          data-testid={`${testId}-status`}
          className={`font-body text-xs mt-1 ${
            status.type === "error" ? "text-rust" : "text-charcoal/60"
          }`}
        >
          {status.message}
        </p>
      )}
      {/* Provider attribution (Geoapify free-tier terms; OSM is Geoapify's data source). */}
      <p data-testid={`${testId}-attribution`} className="font-body text-[10px] text-ink/40 mt-1">
        Geocoding by{" "}
        <a href="https://www.geoapify.com/" target="_blank" rel="noopener noreferrer" className="underline">
          Geoapify
        </a>{" "}
        · ©{" "}
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          OpenStreetMap
        </a>{" "}
        contributors
      </p>
    </div>
  );
}
